import React, { useState, useEffect } from "react";
import { Button, deleteIconButtonClass } from "@/components/ui/button";
import { Link2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { proveedoresAPI, productosAPI, Proveedor, Producto, type ProductoPersonalizadoCreated } from "@/lib/api";
import { filterProductSearchMatches, handleProductSearchHotkey, pedidoProveedorLineaNetoSinIva, pedidoProveedorLineaTotal, toNum } from "@/lib/utils";
import { roundToPeso } from "@/lib/roundToPeso";
import { NumberField } from "@/components/ui/number-field";
import { ComboProductosDialog } from "@/modules/Ventas/ComboProductosDialog";
import {
  ConfiguradorProductoDialog,
  type ConfiguradorProductoExistente,
} from "@/modules/Stock/ConfiguradorProductoDialog";
import {
  comboVisualCodigo,
  comboVisualNombre,
  comboVisualPrecioSum,
  findRegularLine,
  groupComboVisualRows,
  regularLineKey,
  removeLinesByKey,
  syncComboCantidad,
  syncComboPatch,
  type ComboCapableLine,
} from "@/lib/comboLine";
import {
  formatStockTotalLabel,
  productoStockActual,
  productoStockReservado,
} from "@/lib/ventaStockDisponible";
interface PedidoProductoInput {
  codigo: string;
  nombre: string;
  precioBase: number;
  cantidad: number;
  /** % descuento línea (misma regla que Ventas: descuento1, 0–100). */
  descuento1: number;
  descuento2: number;
  /** Agrupa las dos patas en una sola fila. El pedido sigue guardando dos ítems. */
  comboGroup?: string | null;
  lineKey?: string;
  id?: number | null;
}

interface PedidosFormProps {
  onPedidoCreated?: (pedido: any) => void | Promise<void>;
}

export function PedidosForm({ onPedidoCreated }: PedidosFormProps) {
  const [proveedorQuery, setProveedorQuery] = useState("");
  const [showProveedorSuggestions, setShowProveedorSuggestions] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState<Proveedor | null>(null);
  const [proveedores, setProveedores] = useState<Proveedor[]>([]);

  const [productos, setProductos] = useState<PedidoProductoInput[]>([]);
  const [foundProductos, setFoundProductos] = useState<Producto[]>([]);
  const [productoQuery, setProductoQuery] = useState("");
  const [showProductoSuggestions, setShowProductoSuggestions] = useState(false);
  const [cantidad, setCantidad] = useState(1);
  const [loading, setLoading] = useState(false);
  /** Borrador string por línea para Desc. %: evita perder decimales mientras se escribe (p. ej. "20."). */
  const [descuento1Draft, setDescuento1Draft] = useState<Record<string, string>>({});
  const [showComboDialog, setShowComboDialog] = useState(false);
  const [showPersonalizado, setShowPersonalizado] = useState(false);

  const placeLinesAtTop = (current: PedidoProductoInput[], incoming: PedidoProductoInput[]) => {
    let next = current;
    for (const line of [...incoming].reverse()) {
      const idx = next.findIndex((p) => p.codigo === line.codigo && !p.comboGroup);
      if (idx >= 0) {
        const existing = next[idx];
        const rest = next.filter((_, i) => i !== idx);
        next = [{ ...existing, cantidad: existing.cantidad + line.cantidad }, ...rest];
      } else {
        next = [line, ...next];
      }
    }
    return next;
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const provList = await proveedoresAPI.list({ activo: true });
        setProveedores(provList);
      } catch (error) {
        console.error(error);
        toast.error("Error cargando datos iniciales");
      }
    };
    loadData();
  }, []);

  // Misma búsqueda API que Ventas/Presupuestos (search + debounce 300 ms),
  // acotada a Producto.proveedor_id del proveedor del pedido.
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      const proveedorId = selectedProveedor?.id;
      if (!proveedorId || productoQuery.trim().length === 0) {
        setFoundProductos([]);
        return;
      }
      try {
        const results = await productosAPI.list({
          search: productoQuery,
          proveedorId,
        });
        setFoundProductos(results || []);
      } catch (error) {
        console.error(error);
        setFoundProductos([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [productoQuery, selectedProveedor]);

  const filteredProveedores = proveedorQuery.trim()
    ? proveedores.filter(proveedor =>
      proveedor.nombreRazonSocial.toLowerCase().includes(proveedorQuery.toLowerCase()) ||
      proveedor.codigo.toLowerCase().includes(proveedorQuery.toLowerCase())
    )
    : [];

  const filteredProductos = filterProductSearchMatches(foundProductos, productoQuery);

  const selectProveedor = (proveedor: Proveedor) => {
    setSelectedProveedor(proveedor);
    setProveedorQuery(proveedor.nombreRazonSocial);
    setShowProveedorSuggestions(false);
  };

  const addProducto = (producto: Producto) => {
    const precioUnit = roundToPeso(producto.precioBase ?? producto.precio_base);
    setProductos((prev) => {
      const regular = findRegularLine(prev, producto);
      if (regular) {
        const rest = prev.filter((p) => p !== regular);
        return [{ ...regular, cantidad: regular.cantidad + cantidad }, ...rest];
      }
      return placeLinesAtTop(prev, [{
        codigo: producto.codigo,
        nombre: producto.nombre,
        precioBase: precioUnit,
        cantidad,
        descuento1: 0,
        descuento2: 0,
        lineKey: producto.id ? regularLineKey(producto.id) : producto.codigo,
        id: producto.id,
      }]);
    });
    setProductoQuery("");
    setCantidad(1);
    setShowProductoSuggestions(false);
    toast.success("Producto agregado");
  };

  const addComboIndividual = (lines: ComboCapableLine[]) => {
    setProductos((prev) => [
      ...lines.map((l) => ({
        id: l.id,
        codigo: l.codigo,
        nombre: l.nombre,
        precioBase: roundToPeso(l.precioBase),
        cantidad: l.cantidad,
        descuento1: 0,
        descuento2: 0,
        lineKey: l.lineKey,
        comboGroup: l.comboGroup,
      })),
      ...prev,
    ]);
    toast.success(`${lines[0]?.nombre || "A"} + ${lines[1]?.nombre || "B"}`);
  };

  const addPersonalizadoLine = (codigo: string, nombre: string, precioBase: number | string | null | undefined) => {
    setProductos((prev) => placeLinesAtTop(prev, [{
      codigo,
      nombre,
      precioBase: roundToPeso(precioBase),
      cantidad,
      descuento1: 0,
      descuento2: 0,
    }]));
    setCantidad(1);
    toast.success("Ítem personalizado agregado");
  };

  const handlePersonalizadoCreated = (result: ProductoPersonalizadoCreated) => {
    const p = result.producto;
    addPersonalizadoLine(p.codigo, p.nombre, p.precioBase);
    setShowPersonalizado(false);
  };

  const handlePersonalizadoExistente = (existente: ConfiguradorProductoExistente) => {
    addPersonalizadoLine(existente.codigo, existente.nombre, existente.precioBase);
    setShowPersonalizado(false);
  };

  const removeProducto = (key: string) => {
    setProductos(removeLinesByKey(productos, key));
    setDescuento1Draft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const updateCantidad = (key: string, newCantidad: number) => {
    if (newCantidad < 1) return;
    setProductos(syncComboCantidad(productos, key, newCantidad));
  };

  const parsePct = (raw: string): number | null => {
    const t = String(raw).trim().replace(",", ".");
    if (t === "") return 0;
    const n = parseFloat(t);
    return Number.isNaN(n) ? null : n;
  };

  /** % vigente para cálculos: borrador si existe (sin redondear agresivo), si no el valor guardado. */
  const lineDraftKey = (producto: PedidoProductoInput) => producto.lineKey || producto.codigo;

  const effectiveDescuento1 = (producto: PedidoProductoInput) => {
    const draft = descuento1Draft[lineDraftKey(producto)];
    if (draft !== undefined) {
      const n = parsePct(draft);
      if (n !== null) return Math.min(100, Math.max(0, n));
    }
    return toNum(producto.descuento1);
  };

  const commitDescuento1Draft = (key: string) => {
    const draft = descuento1Draft[key];
    if (draft === undefined) return;
    const n = parsePct(draft);
    const val = n === null ? 0 : Math.min(100, Math.max(0, n));
    setProductos((ps) => syncComboPatch(ps, key, { descuento1: val }));
    setDescuento1Draft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });
  };

  const brutoLinea = (producto: PedidoProductoInput) =>
    toNum(producto.precioBase) * toNum(producto.cantidad);

  const lineOpts = (producto: PedidoProductoInput, d1: number) => ({
    precioBase: toNum(producto.precioBase),
    cantidad: toNum(producto.cantidad),
    descuento1: d1,
    descuento2: toNum(producto.descuento2),
  });

  const calcularTotalProductoWithD1 = (
    producto: PedidoProductoInput,
    d1: number,
  ) => pedidoProveedorLineaTotal(lineOpts(producto, d1));

  const calcularTotalProducto = (producto: PedidoProductoInput) =>
    calcularTotalProductoWithD1(producto, effectiveDescuento1(producto));

  const importeDescuentoLinea = (producto: PedidoProductoInput) =>
    brutoLinea(producto) - pedidoProveedorLineaNetoSinIva(
      lineOpts(producto, effectiveDescuento1(producto)),
    );

  const formatMoney = (n: number) =>
    n.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const calcularTotal = () => {
    return productos.reduce((total, p) => total + calcularTotalProducto(p), 0);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    void generarPedido();
  };

  const generarPedido = async () => {
    if (!selectedProveedor) {
      toast.error("Debe seleccionar un proveedor");
      return;
    }
    if (productos.length === 0) {
      toast.error("Debe agregar al menos un producto");
      return;
    }

    try {
      setLoading(true);
      const lineasResueltas = productos.map((p) => ({
        ...p,
        descuento1: effectiveDescuento1(p),
      }));
      const total = roundToPeso(lineasResueltas.reduce(
        (acc, p) => acc + calcularTotalProductoWithD1(p, p.descuento1),
        0,
      ));
      if (!Number.isFinite(total) || total < 0) {
        toast.error("Total del pedido inválido. Revise precios y cantidades de los productos.");
        return;
      }
      // Payload alineado con CamelCaseJSONParser + ProveedorPedidoSerializer (sin claves vacías innecesarias).
      const payload = {
        importe: roundToPeso(total),
        fechaCreacion: new Date().toISOString().split('T')[0],
        estado: 'pendiente' as const,
        productos: lineasResueltas.map((p) => ({
          codigo: p.codigo,
          nombre: p.nombre,
          cantidad: p.cantidad,
          precioBase: roundToPeso(p.precioBase),
          descuento1: Number(toNum(p.descuento1).toFixed(2)),
          descuento2: Number(toNum(p.descuento2).toFixed(2)),
          recibido: false,
          ...(p.comboGroup ? { comboGroup: p.comboGroup } : {}),
        }))
      };

      const result = await proveedoresAPI.createPedido(selectedProveedor.codigo, payload);

      toast.success(`Pedido ${result.nroPedido} creado exitosamente`);

      if (onPedidoCreated) {
        await Promise.resolve(onPedidoCreated(result));
      }

      // Reset form
      setSelectedProveedor(null);
      setProveedorQuery("");
      setProductos([]);
      setDescuento1Draft({});
    } catch (error) {
      console.error(error);
      toast.error("Error al generar pedido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleFormSubmit}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Información del Pedido</h3>

        <div className="grid grid-cols-1 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium mb-1">Buscar Proveedor</label>
            <div className="relative">
              <input
                type="text"
                value={proveedorQuery}
                onChange={(e) => {
                  setProveedorQuery(e.target.value);
                  setShowProveedorSuggestions(true);
                  if (!e.target.value) {
                    setSelectedProveedor(null);
                  }
                }}
                onFocus={() => setShowProveedorSuggestions(true)}
                placeholder="Buscar proveedor por nombre o código..."
                className="w-full px-4 py-2 pr-10 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm"
              />
              {proveedorQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setProveedorQuery("");
                    setSelectedProveedor(null);
                    setShowProveedorSuggestions(false);
                    setProductos([]);
                    setDescuento1Draft({});
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {showProveedorSuggestions && filteredProveedores.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredProveedores.map((proveedor) => (
                  <button
                    key={proveedor.codigo}
                    type="button"
                    onClick={() => selectProveedor(proveedor)}
                    className="w-full px-4 py-2 text-left hover:bg-muted text-sm flex justify-between items-center"
                  >
                    <span>{proveedor.nombreRazonSocial}</span>
                    <span className="text-xs text-muted-foreground">{proveedor.codigo}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedProveedor && (
          <div className="p-4 border border-input rounded-lg bg-muted/50">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Proveedor:</span> {selectedProveedor.nombreRazonSocial}
              </div>
              <div>
                <span className="font-medium">CUIT:</span> {selectedProveedor.cuit}
              </div>
              <div>
                <span className="font-medium">Email:</span> {selectedProveedor.email}
              </div>
              <div>
                <span className="font-medium">Teléfono:</span> {selectedProveedor.telefono}
              </div>
              <div className="col-span-2">
                <span className="font-medium">Dirección:</span> {selectedProveedor.domicilio}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Agregar Productos</h3>
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={productoQuery}
              onChange={(e) => {
                setProductoQuery(e.target.value);
                setShowProductoSuggestions(true);
              }}
              onFocus={() => setShowProductoSuggestions(true)}
              onKeyDown={(e) =>
                handleProductSearchHotkey(e, filteredProductos.length, () =>
                  addProducto(filteredProductos[0])
                )
              }
              placeholder={selectedProveedor ? "Buscar producto..." : "Primero seleccione un proveedor"}
              disabled={!selectedProveedor || loading}
              className="w-full px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {showProductoSuggestions && filteredProductos.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-background border border-input rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {filteredProductos.map((producto) => (
                  <button
                    key={producto.codigo}
                    type="button"
                    onClick={() => addProducto(producto)}
                    className="w-full px-4 py-2 text-left hover:bg-muted text-sm"
                  >
                    <div className="flex justify-between items-center gap-2">
                      <span className="truncate">
                        <span className="text-xs text-muted-foreground mr-2">{producto.codigo}</span>
                        {producto.nombre}
                      </span>
                      <span className="text-xs text-muted-foreground shrink-0">
                        ${toNum(producto.precioBase ?? producto.precio_base).toLocaleString("es-AR", {
                          minimumFractionDigits: 2,
                        })}
                        {" | "}
                        {formatStockTotalLabel(
                          productoStockActual(producto),
                          productoStockReservado(producto),
                        )}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <NumberField
            min="1"
            value={cantidad}
            onChange={(e) => {
              const val = toNum(e.target.value);
              setCantidad(val > 0 ? val : 1);
            }}
            disabled={!selectedProveedor || loading}
            className="w-24 px-4 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-sm disabled:opacity-50"
            placeholder="Cant."
          />
          <Button
            type="button"
            variant="outline"
            title="Ítem personalizado"
            aria-label="Ítem personalizado"
            disabled={!selectedProveedor || loading}
            onClick={() => setShowPersonalizado(true)}
          >
            <Plus className="h-4 w-4 mr-1" />
            Pers.
          </Button>
          <Button
            type="button"
            variant="outline"
            title="Combinar productos"
            aria-label="Combinar productos"
            disabled={!selectedProveedor || loading}
            onClick={() => setShowComboDialog(true)}
          >
            <Link2 className="h-4 w-4 mr-1" />
            Combo
          </Button>
        </div>

        {productos.length > 0 && (
          <div className="border border-input rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  <th className="text-left py-2 px-4 text-sm font-medium">Código</th>
                  <th className="text-left py-2 px-4 text-sm font-medium">Producto</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Cantid.</th>
                  <th className="text-center py-2 px-4 text-sm font-medium">Desc. %</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Desc. $</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Precio Base</th>
                  <th className="text-right py-2 px-4 text-sm font-medium">Total</th>
                  <th className="w-12"></th>
                </tr>
              </thead>
              <tbody>
                {groupComboVisualRows(productos).map((row) => {
                  const producto = row.legs[0];
                  const codigo = row.isCombo ? comboVisualCodigo(row.legs) : producto.codigo;
                  const nombre = row.isCombo ? comboVisualNombre(row.legs) : producto.nombre;
                  const precio = row.isCombo ? comboVisualPrecioSum(row.legs) : producto.precioBase;
                  const descPesos = row.legs.reduce((s, p) => s + importeDescuentoLinea(p), 0);
                  const totalLinea = row.legs.reduce((s, p) => s + calcularTotalProducto(p), 0);
                  const draftKey = lineDraftKey(producto);
                  return (
                  <tr key={row.key} className="border-t border-input">
                    <td className="py-2 px-4 text-sm whitespace-pre-line">{codigo}</td>
                    <td className="py-2 px-4 text-sm whitespace-normal break-words">{nombre}</td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min="1"
                        value={producto.cantidad}
                        onChange={(e) => updateCantidad(row.key, toNum(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-center">
                      <NumberField
                        min={0}
                        max={100}
                        step={0.5}
                        value={
                          descuento1Draft[draftKey] !== undefined
                            ? descuento1Draft[draftKey]
                            : producto.descuento1 === 0
                              ? ""
                              : String(producto.descuento1)
                        }
                        onFocus={() => {
                          setDescuento1Draft((d) => {
                            if (d[draftKey] !== undefined) return d;
                            const show =
                              producto.descuento1 === 0
                                ? ""
                                : String(producto.descuento1);
                            const next = { ...d };
                            for (const leg of row.legs) next[lineDraftKey(leg)] = show;
                            return next;
                          });
                        }}
                        onChange={(e) =>
                          setDescuento1Draft((d) => {
                            const next = { ...d };
                            for (const leg of row.legs) next[lineDraftKey(leg)] = e.target.value;
                            return next;
                          })
                        }
                        onBlur={() => {
                          commitDescuento1Draft(row.key);
                          setDescuento1Draft((d) => {
                            const next = { ...d };
                            for (const leg of row.legs) delete next[lineDraftKey(leg)];
                            return next;
                          });
                        }}
                        className="w-[4.25rem] px-2 py-1 border border-input bg-background rounded text-sm text-center"
                      />
                    </td>
                    <td className="py-2 px-4 text-sm text-right text-muted-foreground tabular-nums">
                      ${formatMoney(descPesos)}
                    </td>
                    <td className="py-2 px-4 text-sm text-right">
                      ${precio.toLocaleString('es-AR')}
                    </td>
                    <td className="py-2 px-4 text-sm text-right font-medium tabular-nums">
                      ${formatMoney(totalLinea)}
                    </td>
                    <td className="py-2 px-4">
                      <button
                        type="button"
                        onClick={() => removeProducto(row.key)}
                        className={deleteIconButtonClass}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                  );
                })}
                <tr className="border-t-2 border-input bg-muted/50">
                  <td colSpan={6} className="py-3 px-4 text-right font-semibold">Total (IVA 21%):</td>
                  <td className="py-3 px-4 text-right font-bold text-lg tabular-nums">
                    ${formatMoney(calcularTotal())}
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex gap-2 justify-end">
        <Button
          type="submit"
          disabled={!selectedProveedor || productos.length === 0 || loading}
          className="bg-gradient-to-br from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
        >
          {loading ? "Generando..." : "Generar Pedido"}
        </Button>
      </div>
      <ComboProductosDialog
        open={showComboDialog}
        onOpenChange={setShowComboDialog}
        initialQty={cantidad}
        priceSource="base"
        onAdd={addComboIndividual}
      />
      <ConfiguradorProductoDialog
        open={showPersonalizado}
        onOpenChange={setShowPersonalizado}
        onCreated={handlePersonalizadoCreated}
        onOpenExistente={handlePersonalizadoExistente}
        existenteActionLabel="Usar en el pedido"
        title="Ítem personalizado"
        confirmLabel="Crear y agregar"
      />
    </form>
  );
}